import { useState, useCallback, useContext } from 'react';
import { Modal, Button, Row, Col, Alert, ProgressBar, Table } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { read, utils } from 'xlsx';
import { db } from 'config/firebase';
import { collection, doc, writeBatch, serverTimestamp, getDocs } from 'firebase/firestore';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext';
import { toast } from 'react-toastify';
import Flex from 'components/common/Flex';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';

const FUNCTIONS_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

const REQUIRED_COLUMNS = ['SKU'];
const OPTIONAL_COLUMNS = ['FulfillmentCost', 'Title', 'Quantity', 'FulfillmentCenter', 'Category'];

export default function ImportModal({ show, onHide, onComplete }) {
  const { user } = useContext(FirebaseAuthContext);

  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState('');
  const [rawData, setRawData] = useState([]);
  const [validatedData, setValidatedData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);

  // CSV Parsing
  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach((file) => {
      setFileName(file.name);
      const reader = new FileReader();

      reader.onerror = () => toast.error('File reading failed');
      reader.onload = () => {
        try {
          const binaryStr = reader.result;
          const workbook = read(binaryStr, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = utils.sheet_to_json(worksheet);

          if (data.length === 0) {
            toast.error('CSV file is empty');
            return;
          }

          // Validate required columns
          const firstRow = data[0];
          const missingColumns = REQUIRED_COLUMNS.filter(col => !(col in firstRow));

          if (missingColumns.length > 0) {
            toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
            return;
          }

          // Validate and transform data
          const validated = data.map((row, index) => {
            const errors = [];

            // Validate SKU
            const sku = String(row.SKU || '').trim();
            if (!sku) {
              errors.push('SKU is required');
            }

            // Parse fulfillment cost
            let fc = null;
            if (row.FulfillmentCost !== undefined && row.FulfillmentCost !== '') {
              const parsed = parseFloat(String(row.FulfillmentCost).replace(/[$,]/g, ''));
              if (isNaN(parsed) || parsed < 0) {
                errors.push('Invalid FulfillmentCost');
              } else {
                fc = parsed;
              }
            }

            return {
              rowNumber: index + 2,
              sku,
              fulfillmentCost: fc,
              title: row.Title || null,
              quantity: row.Quantity ? parseInt(row.Quantity) : null,
              fulfillmentCenter: row.FulfillmentCenter || null,
              category: row.Category || null,
              errors,
              isValid: errors.length === 0
            };
          });

          setRawData(data);
          setValidatedData(validated);
          setStep(2);
          toast.success(`Loaded ${data.length} rows from ${file.name}`);
        } catch (error) {
          console.error('Parse error:', error);
          toast.error('Failed to parse file');
        }
      };

      reader.readAsArrayBuffer(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.csv, .xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv',
    multiple: false
  });

  // Import to Firebase
  const handleImport = async () => {
    const validRows = validatedData.filter(r => r.isValid);

    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    setStep(3);

    try {
      // Step 1: Load existing baseline for SKUs being imported (for change tracking)
      const skusToImport = validRows.map(r => r.sku);
      const existingBaseline = new Map();

      // Query existing baseline once and build lookup map
      const baselineSnapshot = await getDocs(collection(db, 'fcBaseline'));
      const skuSet = new Set(skusToImport);
      baselineSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (skuSet.has(data.sku)) {
          existingBaseline.set(data.sku, data.fulfillmentCost);
        }
      });

      // Step 2: Batch write to fcBaseline collection
      const BATCH_SIZE = 500;
      let processed = 0;
      let success = 0;
      let failed = 0;

      for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = validRows.slice(i, i + BATCH_SIZE);

        chunk.forEach(row => {
          const docRef = doc(collection(db, 'fcBaseline'), row.sku);
          batch.set(docRef, {
            sku: row.sku,
            fulfillmentCost: row.fulfillmentCost,
            title: row.title,
            quantity: row.quantity,
            fulfillmentCenter: row.fulfillmentCenter,
            category: row.category,
            source: 'cart.com',
            importedBy: user?.email || 'unknown',
            importedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }, { merge: true });
        });

        await batch.commit();
        processed += chunk.length;
        success += chunk.length;
        setImportProgress(Math.round((processed / validRows.length) * 80)); // 80% for import
      }

      // Step 3: Track FC changes for sales cliff detection
      const fcChanges = [];
      validRows.forEach(row => {
        if (row.fulfillmentCost !== null) {
          const oldFC = existingBaseline.get(row.sku);
          // Track if there was an old value and it's different from new
          if (oldFC !== undefined && oldFC !== row.fulfillmentCost) {
            fcChanges.push({
              sku: row.sku,
              oldFC: oldFC,
              newFC: row.fulfillmentCost
            });
          }
        }
      });

      // Call tracking endpoint if there are changes
      let changesTracked = 0;
      if (fcChanges.length > 0) {
        try {
          const response = await fetch(`${FUNCTIONS_URL}/fcOptimizerTrackChanges`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ changes: fcChanges })
          });

          if (response.ok) {
            const result = await response.json();
            changesTracked = result.tracked || 0;
            console.log(`Tracked ${changesTracked} FC changes for sales cliff detection`);
          }
        } catch (trackError) {
          console.warn('Failed to track FC changes:', trackError);
          // Don't fail the import if tracking fails
        }
      }

      setImportProgress(100);

      setImportResults({
        success,
        failed,
        total: validRows.length,
        changesTracked
      });

      const trackingMsg = changesTracked > 0 ? ` (${changesTracked} FC changes tracked for monitoring)` : '';
      toast.success(`Imported ${success} records to baseline${trackingMsg}`);

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error.message}`);
      setImportResults({
        success: 0,
        failed: validatedData.filter(r => r.isValid).length,
        error: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFileName('');
    setRawData([]);
    setValidatedData([]);
    setImportProgress(0);
    setImportResults(null);
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  const validCount = validatedData.filter(r => r.isValid).length;
  const errorCount = validatedData.filter(r => !r.isValid).length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 1 && 'Import Current FC from Cart.com'}
          {step === 2 && 'Review Import Data'}
          {step === 3 && 'Importing...'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Step 1: Upload */}
        {step === 1 && (
          <>
            <Alert variant="info" className="mb-3">
              <p className="mb-1"><strong>How to export from Cart.com:</strong></p>
              <ol className="mb-0 ps-3">
                <li>Go to Inventory &gt; Inventory Raw</li>
                <li>Export as CSV</li>
                <li>Upload the file here</li>
              </ol>
            </Alert>

            <Alert variant="secondary" className="mb-3">
              <strong>Required column:</strong> SKU<br />
              <strong>Optional columns:</strong> FulfillmentCost, Title, Quantity, FulfillmentCenter, Category
            </Alert>

            <div
              {...getRootProps()}
              className={`dropzone-area py-5 text-center border rounded ${isDragActive ? 'border-primary bg-soft-primary' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <input {...getInputProps()} />
              <Flex justifyContent="center" alignItems="center" flexDirection="column">
                <img src={cloudUpload} alt="" width={48} className="mb-3" />
                <p className="fs-0 mb-0 text-700">
                  {isDragActive ? 'Drop the file here' : 'Drag CSV file here or click to select'}
                </p>
                <p className="fs--1 text-muted">Supports .csv, .xls, .xlsx</p>
              </Flex>
            </div>
          </>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <>
            <Alert variant={errorCount > 0 ? 'warning' : 'success'} className="mb-3">
              <Row className="align-items-center">
                <Col>
                  <strong>{validCount}</strong> valid rows, <strong>{errorCount}</strong> errors
                </Col>
              </Row>
            </Alert>

            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table size="sm" hover className="fs--1">
                <thead className="bg-200 sticky-top">
                  <tr>
                    <th>Row</th>
                    <th>SKU</th>
                    <th>FC</th>
                    <th>Title</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {validatedData.slice(0, 100).map((row) => (
                    <tr key={row.rowNumber} className={row.isValid ? '' : 'table-danger'}>
                      <td>{row.rowNumber}</td>
                      <td className="fw-semi-bold">{row.sku}</td>
                      <td>{row.fulfillmentCost != null ? `$${row.fulfillmentCost.toFixed(2)}` : '-'}</td>
                      <td className="text-truncate" style={{ maxWidth: '200px' }}>{row.title || '-'}</td>
                      <td>
                        {row.isValid ? (
                          <span className="badge bg-success">Valid</span>
                        ) : (
                          <span className="badge bg-danger" title={row.errors.join(', ')}>Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {validatedData.length > 100 && (
                <p className="text-muted text-center fs--1">
                  Showing 100 of {validatedData.length} rows
                </p>
              )}
            </div>
          </>
        )}

        {/* Step 3: Importing */}
        {step === 3 && (
          <div className="text-center py-4">
            {importing ? (
              <>
                <h5 className="mb-3">Importing to Firebase...</h5>
                <ProgressBar
                  now={importProgress}
                  label={`${importProgress}%`}
                  className="mb-3"
                  style={{ height: '30px' }}
                />
                <p className="text-muted">Please wait, do not close this window</p>
              </>
            ) : importResults ? (
              <>
                {importResults.error ? (
                  <Alert variant="danger">
                    <h5>Import Failed</h5>
                    <p className="mb-0">{importResults.error}</p>
                  </Alert>
                ) : (
                  <Alert variant="success">
                    <h5>Import Complete!</h5>
                    <p className="mb-0">
                      Successfully imported <strong>{importResults.success}</strong> records
                    </p>
                    {importResults.changesTracked > 0 && (
                      <p className="mb-0 mt-2 text-muted">
                        <small>
                          📊 {importResults.changesTracked} FC changes tracked for sales cliff monitoring
                        </small>
                      </p>
                    )}
                  </Alert>
                )}
              </>
            ) : null}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 1 && (
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        )}

        {step === 2 && (
          <>
            <Button variant="outline-secondary" onClick={handleReset}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={validCount === 0}
            >
              Import {validCount} Records
            </Button>
          </>
        )}

        {step === 3 && !importing && (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
